import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SignIn, SignUp, useUser } from "@/lib/clerkShim";
import { motion } from "framer-motion";
import { Shield, Crown, Loader2 } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";
import { STRIPE_TIERS, type StripeTierKey } from "@/config/stripeTiers";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";

const Auth = () => {
  const [searchParams] = useSearchParams();
  const isCeoFlow = searchParams.get("persona") === "ceo";
  const selectedPlan = searchParams.get("plan") as StripeTierKey | null;
  const mode = searchParams.get("mode"); // "sign-up" or default to sign-in

  const { user, isLoaded } = useUser();
  const { primaryRole, loading: rolesLoading } = useUserRole();
  const navigate = useNavigate();

  // After auth, handle CEO checkout or role-based redirect
  useEffect(() => {
    if (user && isLoaded && !rolesLoading) {
      if (isCeoFlow && selectedPlan && selectedPlan !== "enterprise") {
        handleCheckout();
      } else if (isCeoFlow) {
        api.post("/check-subscription").then(() => {
          navigate("/ceo");
        }).catch(() => {
          navigate("/ceo");
        });
      } else {
        switch (primaryRole) {
          case 'internal_admin':
          case 'internal_reviewer':
            navigate("/admin");
            break;
          case 'ceo':
            navigate("/ceo");
            break;
          case 'seller':
            navigate("/seller");
            break;
          case 'buyer':
            navigate("/buyer");
            break;
          default:
            navigate("/dashboard");
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isLoaded, primaryRole, rolesLoading]);

  const handleCheckout = async () => {
    if (!selectedPlan || selectedPlan === "enterprise") return;
    const tier = STRIPE_TIERS[selectedPlan];
    if (!tier?.price_id) return;

    try {
      const data = await api.post<{ url?: string; error?: string }>("/create-checkout", {
        priceId: tier.price_id,
      });

      if (data?.error) {
        const isAlreadySubscribed = typeof data.error === "string" && data.error.toLowerCase().includes("already have an active subscription");
        if (isAlreadySubscribed) {
          toast.success("You already have an active subscription!");
          navigate("/ceo");
          return;
        }
        throw new Error(data.error);
      }

      if (data?.url) {
        const opened = window.open(data.url, '_blank');
        if (!opened) {
          window.location.href = data.url;
        }
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to start checkout";
      console.error("Checkout error:", err);
      toast.error(message);
      navigate("/ceo");
    }
  };

  const isSignUp = isCeoFlow || mode === "sign-up";
  const tierInfo = selectedPlan && selectedPlan !== "enterprise" ? STRIPE_TIERS[selectedPlan] : null;

  // User is authenticated — show a spinner while we resolve roles and redirect
  if (user && isLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      {/* Background effects */}
      <div className="absolute inset-0 grid-pattern opacity-5" />
      <div className="absolute inset-0 radial-gradient-overlay" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${
            isCeoFlow
              ? 'bg-gradient-to-br from-amber-500 to-orange-500'
              : 'bg-primary'
          }`}>
            {isCeoFlow ? (
              <Crown className="h-7 w-7 text-white" />
            ) : (
              <Shield className="h-7 w-7 text-primary-foreground" />
            )}
          </div>
          <span className="font-display text-2xl font-bold tracking-tight">
            PROVENANCE
          </span>
        </div>

        {/* Selected plan badge */}
        {tierInfo && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-4"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 text-sm">
              <Crown className="h-4 w-4 text-amber-500" />
              <span className="text-amber-600 dark:text-amber-400 font-medium">
                {tierInfo.name} Plan — {tierInfo.price}/mo
              </span>
            </span>
          </motion.div>
        )}

        {/* Clerk auth component */}
        <div className="flex justify-center">
          {isSignUp ? (
            <SignUp
              routing="hash"
              afterSignUpUrl="/auth"
              appearance={{
                elements: {
                  rootBox: "w-full",
                  card: "bg-surface-dark border border-border rounded-2xl shadow-xl",
                },
              }}
            />
          ) : (
            <SignIn
              routing="hash"
              afterSignInUrl="/auth"
              appearance={{
                elements: {
                  rootBox: "w-full",
                  card: "bg-surface-dark border border-border rounded-2xl shadow-xl",
                },
              }}
            />
          )}
        </div>

        {/* Back link */}
        <div className="mt-6 text-center">
          <a
            href={isCeoFlow ? "/for-founders" : "/"}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            &larr; {isCeoFlow ? "Back to For Founders" : "Back to home"}
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default Auth;
